import type { SubscriptionStatus, DiscountsResponse, SMPSubscriptionLineItem, SMPComponentization } from '@viome/viome-base/src/usecase/SMP/SMP.model';
import { getFormulaImageUrl } from '@/utils/nutrition/getFormulaImageUrl';
import { resolveComponentizationEligibleSkus } from '@/constants/componentizationSkus';

// ============================================
// Kit vs supplement subscription
// ============================================

// Test-kit subscription SKUs (Phase 1 Annual Kit: FBI + GI + OHI). A
// subscription is a kit/test subscription if any line item is one of these.
const KIT_SKUS = ['FBI', 'GI', 'OHI'];

export const isKitSubscription = (
  lineItems?: Pick<SMPSubscriptionLineItem, 'sku'>[],
): boolean => !!lineItems?.some((item) => KIT_SKUS.includes(item.sku));

// ============================================
// Componentization eligibility (SMP domain)
// ============================================

// Mirrors src/utils/componentizationEligibility.ts (Misc.Subscription model,
// used by the Account page) — same shared SKU list, different input shape,
// since SMPSubscriptionDetail has no top-level sku (see
// SMP_COMPONENTIZATION_TECH_SPEC_v1.md §2.1).
// Prefer getInit `componentizationEligibleSkus` when passed; local list is fallback.

export const isEligibleForComponentization = (
  lineItems?: Pick<SMPSubscriptionLineItem, 'sku'>[],
  eligibleSkus?: string[] | null,
): boolean => {
  const skus = resolveComponentizationEligibleSkus(eligibleSkus);
  return !!lineItems?.some((item) => skus.includes(item.sku));
};

/**
 * Resolve componentization eligibility for a subscription detail, preferring the
 * BE-driven `componentization` object from the detail API. When the API returns
 * it, its `componentizationEnabled` flag is the source of truth; otherwise fall
 * back to the local SKU allowlist check.
 */
export const isComponentizationEligible = (
  componentization?: SMPComponentization | null,
  lineItems?: Pick<SMPSubscriptionLineItem, 'sku'>[],
  eligibleSkus?: string[] | null,
): boolean => {
  if (componentization && typeof componentization.componentizationEnabled === 'boolean') {
    return componentization.componentizationEnabled;
  }
  return isEligibleForComponentization(lineItems, eligibleSkus);
};

// ============================================
// Subscription Status Helpers
// ============================================

export const SUBSCRIPTION_STATUS = {
  ACTIVE: 'active',
  PAUSED: 'paused',
  SKIPPED: 'skipped',
  CANCELLED: 'cancelled',
  INACTIVE: 'inactive',
} as const;

export const isSubscriptionActive = (status: SubscriptionStatus): boolean =>
  status === SUBSCRIPTION_STATUS.ACTIVE;

export const isSubscriptionPaused = (status: SubscriptionStatus): boolean =>
  status === SUBSCRIPTION_STATUS.PAUSED;

export const isSubscriptionSkipped = (status: SubscriptionStatus): boolean =>
  status === SUBSCRIPTION_STATUS.SKIPPED;

/** API returns 'inactive' for cancelled subscriptions (legacy: SUBSCRIPTION_CANCELLED = 'inactive') */
export const isSubscriptionCancelled = (status: SubscriptionStatus): boolean =>
  status === SUBSCRIPTION_STATUS.CANCELLED || status === SUBSCRIPTION_STATUS.INACTIVE;

/** Returns true if subscription is in an active-like state (active, paused, or skipped) */
export const isSubscriptionActiveState = (status: SubscriptionStatus): boolean =>
  isSubscriptionActive(status) || isSubscriptionPaused(status) || isSubscriptionSkipped(status);

// ============================================
// Status Badge Styles
// ============================================

export const getStatusBadgeClassName = (status: SubscriptionStatus): string => {
  switch (status) {
    case SUBSCRIPTION_STATUS.ACTIVE:
      return 'bg-[#009E82] text-white';
    case SUBSCRIPTION_STATUS.PAUSED:
      return 'bg-black text-white';
    case SUBSCRIPTION_STATUS.SKIPPED:
      return 'bg-warning text-white';
    case SUBSCRIPTION_STATUS.CANCELLED:
    case SUBSCRIPTION_STATUS.INACTIVE:
      return 'bg-tertiary text-white';
    default:
      return 'bg-tertiary text-white';
  }
};

export const getStatusDisplayLabel = (status: SubscriptionStatus): string => {
  if (isSubscriptionCancelled(status)) return 'Cancelled';
  return status;
};

// ============================================
// Date Formatting
// ============================================

const MONTH_NAMES_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const MONTH_NAMES_FULL = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** Format date to "Mon D, YYYY" (e.g., "Jan 1, 2026") */
export const formatSMPDate = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    
    const month = MONTH_NAMES_SHORT[date.getMonth()];
    const day = date.getDate();
    const year = date.getFullYear();
    
    return `${month} ${day}, ${year}`;
  } catch {
    return dateString;
  }
};

/** Format date to "Month D, YYYY" (e.g., "January 1, 2026") */
export const formatSMPDateFull = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;

    const month = MONTH_NAMES_FULL[date.getMonth()];
    const day = date.getDate();
    const year = date.getFullYear();

    return `${month} ${day}, ${year}`;
  } catch {
    return dateString;
  }
};

// ============================================
// Price Formatting
// ============================================

/** Format price from cents to dollars (e.g., 19900 → "$199.00") */
export const formatPriceFromCents = (priceInCents: number): string => {
  const dollars = priceInCents / 100;
  return `$${dollars.toFixed(2)}`;
};

/** Format price already in dollars (e.g., 199 → "$199.00") */
export const formatPrice = (price: number): string => {
  return `$${price.toFixed(2)}`;
};

/**
 * `transaction_details.tax` is returned in dollars; line-item `price` is in cents.
 * Convert tax to cents before adding to line-item totals.
 */
export const taxDollarsToCents = (taxDollars: number): number =>
  Math.round(taxDollars * 100);

// ============================================
// Address Formatting
// ============================================

export const formatShippingAddress = (address: {
  first_name?: string;
  last_name?: string | null;
  street1: string;
  street2?: string | null;
  city: string;
  province: string;
  province_code?: string | null;
  zip: string;
  country?: string;
}): string[] => {
  const name = [address.first_name, address.last_name]
    .filter(Boolean)
    .join(' ');

  return [
    name,
    address.street1,
    address.street2,
    `${address.city}, ${address.province_code || address.province} ${address.zip}`,
  ].filter(Boolean) as string[];
};

/** Format an order address (SMPOrderAddress — different field names from Bold address) */
export const formatOrderAddress = (address: {
  full_name?: string;
  first_name?: string;
  last_name?: string | null;
  address_line1?: string;
  address_line2?: string | null;
  street1?: string;
  street2?: string | null;
  city: string;
  state?: string;
  province_code?: string | null;
  zip: string;
}): string[] => {
  const fullName =
    address.full_name ||
    [address.first_name, address.last_name].filter(Boolean).join(' ');

  return [
    fullName,
    address.address_line1 || address.street1,
    address.address_line2 || address.street2,
    `${address.city}, ${address.state || address.province_code || ''} ${address.zip}`,
  ].filter(Boolean) as string[];
};

/** Calculate new order date by adding 1 month (for skip order flow) */
export const getNewNextOrderDate = (nextOrderDatetime: string): Date => {
  const date = new Date(nextOrderDatetime);
  date.setMonth(date.getMonth() + 1);
  return date;
};

/** Format a Date object to "Mon D, YYYY" */
export const formatDateToSMPDate = (date: Date): string => {
  const month = MONTH_NAMES_SHORT[date.getMonth()];
  const day = date.getDate();
  const year = date.getFullYear();
  return `${month} ${day}, ${year}`;
};

/** Format a Date object to "YYYY-MM-DD" for API payloads */
export const formatDateWithDashes = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// ============================================
// Carrier / Tracking Helpers
// ============================================

const UPS_PATTERN = /^(1Z)[0-9A-Z]{16}$/;
const USPS_PATTERN = /^(94|93|92|95)\d{20}$/;

/** Get carrier tracking URL from a tracking ID */
export const getTrackingUrl = (trackingId: string): string | null => {
  if (UPS_PATTERN.test(trackingId)) {
    return `https://www.ups.com/track?tracknum=${trackingId}`;
  }
  if (USPS_PATTERN.test(trackingId)) {
    return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${trackingId}`;
  }
  return null;
};

/** Get carrier name from a tracking ID */
export const getCarrierName = (trackingId: string): string | null => {
  if (UPS_PATTERN.test(trackingId)) return 'UPS';
  if (USPS_PATTERN.test(trackingId)) return 'USPS';
  return null;
};

// ============================================
// Product Type & Display Helpers
// ============================================

const KIT_TYPE = 'KIT';

/**
 * Maps a product title to a radial gradient background class for the product thumbnail.
 * Gradient colors are matched to each product's brand identity.
 */
export const getProductGradientClass = (title: string): string => {
  const lower = title.toLowerCase();

  if (lower.includes('supplement'))
    return 'bg-[radial-gradient(circle_12px_at_71.65%_69.82%,#D5F6D9_0%,#EBEDE0_100%),linear-gradient(180deg,#FFFFFF_0%,rgba(255,255,255,0)_100%)]';

  if (lower.includes('biotics') || lower.includes('gut formula'))
    return 'bg-[radial-gradient(circle_12px_at_71.65%_69.82%,#EFE6B6_0%,#F0E9E3_100%),linear-gradient(180deg,#FFFFFF_0%,rgba(255,255,255,0)_100%)]';

  if (lower.includes('toothpaste') || lower.includes('gel'))
    return 'bg-[radial-gradient(circle_12px_at_71.65%_69.82%,#CCE3E8_0%,#EEE9E1_100%),linear-gradient(180deg,#FFFFFF_0%,rgba(255,255,255,0)_100%)]';

  if (lower.includes('lozenge'))
    return 'bg-[radial-gradient(circle_12px_at_71.65%_69.82%,#DCF3FF_0%,#E0E2F0_100%),linear-gradient(180deg,#FFFFFF_0%,rgba(255,255,255,0)_100%)]';

  return '';
};

const BRANDED_PRODUCT_NAMES: Record<string, string> = {
  'Supplements Plan': 'Precision Supplements™',
  'Biotics Plan': 'Gut Formula',
  'Lozenges Plan': 'Oral Lozenges',
  'Toothpaste/Gel Plan': 'Toothpaste & Gel',
};

const PRODUCT_IMAGE_MAP: Record<string, string> = {
  'Supplements Plan': 'supplement.png',
  'Biotics Plan': 'gut_formula.png',
  'Lozenges Plan': 'oral_lozenges.png',
  'Toothpaste/Gel Plan': 'toothpaste_and_gel.png',
};

/**
 * Maps API product titles to their branded equivalents with ™ symbols.
 * If the title already contains ™ or has no known mapping, returns it as-is.
 */
export const formatProductName = (title: string): string => {
  return BRANDED_PRODUCT_NAMES[title] ?? title;
};

export const getProductImageForSMP = (title: string): string => {
  return PRODUCT_IMAGE_MAP[title] ? getFormulaImageUrl(PRODUCT_IMAGE_MAP[title]) : '';
};

/**
 * Returns the supply label for a plan component based on its `type` field.
 * KIT products ship every 12 orders; supplements ship as 30-day supply.
 */
export const getComponentSupplyLabel = (component: { type: string }): string => {
  return component.type === KIT_TYPE ? 'every 6 months' : 'monthly';
};

export const getComponentOldSupplyLabel = (component: { type: string }): string => {
 return component.type === KIT_TYPE ? 'Every 12 orders' : '30-day supply';
};

/**
 * Formats subscription detail `billingPolicy` into supply copy.
 * MONTH/1 → "monthly"; MONTH/6 → "every 6 months".
 * Returns null when policy is missing so callers can fall back.
 */
export const formatBillingPolicySupplyText = (
  billingPolicy?: { interval: string; intervalCount: number } | null,
): string | null => {
  const count = billingPolicy?.intervalCount;
  const interval = billingPolicy?.interval?.toUpperCase();
  if (!interval || !count || count < 1) return null;

  if (interval === 'MONTH') {
    return count === 1 ? 'monthly' : `every ${count} months`;
  }
  if (interval === 'YEAR') {
    return count === 1 ? 'yearly' : `every ${count} years`;
  }
  if (interval === 'WEEK') {
    return count === 1 ? 'weekly' : `every ${count} weeks`;
  }
  if (interval === 'DAY') {
    return count === 1 ? 'daily' : `every ${count} days`;
  }
  return null;
};

export interface DiscountBreakdownEntry {
  code: string;
  amountCents: number;
}

/**
 * Applies discounts sequentially in the order returned by the API.
 * Each percentage discount is computed against the running total after
 * earlier discounts have been deducted.
 */
export const computeDiscountBreakdown = (
  discounts: DiscountsResponse[],
  subTotalCents: number,
): DiscountBreakdownEntry[] => {
  let remaining = subTotalCents;
  return discounts.map((d) => {
    const val = parseFloat(d.discountValue);
    const amountCents = d.discountType === 'percentage'
      ? Math.round(remaining * (val / 100))
      : Math.round(val * 100);
    const applied = Math.min(amountCents, remaining);
    remaining = Math.max(0, remaining - applied);
    return { code: d.discountCodeName, amountCents: applied };
  });
};
